import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { HardwareTypeDetailState } from './useHardwareTypeDetail';

/** The Overview tab: identity, behaviour (reboot command, ANEP references) and dates; edited in place. */
export const HardwareTypeOverviewTab: React.FC<{ state: HardwareTypeDetailState }> = ({ state }) => {
  const { formatDateTime, formatNumber, t } = useI18n();
  const { hardwareType, isEditing, isLoading, errors } = state;
  if (!hardwareType) return null;

  return (
    <div className="tab-content">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('deviceTypes.detail.identity')}</h3>
          <p>{t('deviceTypes.detail.identityIntro')}</p>
        </header>
        {isEditing ? (
          <div className="cu-grid">
            <div className="cu-field">
              <label htmlFor="hwCode">{t('deviceTypes.deviceTypeCode')} *</label>
              <input
                type="text"
                id="hwCode"
                data-testid="field-hardware-type-code-edit"
                value={state.code}
                onChange={(e) => state.setCode(e.target.value.toUpperCase())}
                className={`cu-input mono${errors.code ? ' is-invalid' : ''}`}
                placeholder={t('deviceTypes.detail.codePlaceholder')}
                maxLength={20}
                disabled={isLoading}
              />
              {errors.code && <span className="cu-error" data-testid="error-hardware-type-code">{errors.code}</span>}
            </div>
            <div className="cu-field">
              <label htmlFor="hwStatus">{t('deviceTypes.detail.statusLabel')}</label>
              <select id="hwStatus" className="cu-input" data-testid="field-hardware-type-status-edit" value={state.isActive ? 'active' : 'inactive'} onChange={(e) => state.setIsActive(e.target.value === 'active')} disabled={isLoading}>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>
            <div className="cu-field cu-field-wide">
              <label htmlFor="hwName">{t('deviceTypes.name')} *</label>
              <input
                type="text"
                id="hwName"
                data-testid="field-hardware-type-name-edit"
                value={state.name}
                onChange={(e) => state.setName(e.target.value)}
                className={`cu-input${errors.name ? ' is-invalid' : ''}`}
                placeholder={t('deviceTypes.hardwareTypeName')}
                disabled={isLoading}
              />
              {errors.name && <span className="cu-error" data-testid="error-hardware-type-name">{errors.name}</span>}
            </div>
            <div className="cu-field cu-field-wide">
              <label htmlFor="hwDescription">{t('common.description')}</label>
              <textarea
                id="hwDescription"
                data-testid="field-hardware-type-description-edit"
                value={state.description}
                onChange={(e) => state.setDescription(e.target.value)}
                rows={2}
                placeholder={t('deviceTypes.hardwareTypeDescription')}
                className={`cu-input${errors.description ? ' is-invalid' : ''}`}
                disabled={isLoading}
              />
              {errors.description && <span className="cu-error">{errors.description}</span>}
            </div>
          </div>
        ) : (
          <dl className="tpd-facts">
            <div className="tpd-fact">
              <dt>{t('deviceTypes.deviceTypeCode')}</dt>
              <dd><span className="type-badge tpl-type">{hardwareType.code}</span></dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('deviceTypes.detail.statusLabel')}</dt>
              <dd>
                <span className={`status-badge ${hardwareType.is_active ? 'active' : 'inactive'}`}>
                  {hardwareType.is_active ? t('common.active') : t('common.inactive')}
                </span>
              </dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('deviceTypes.name')}</dt>
              <dd>{hardwareType.name}</dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('deviceTypes.modelReferences')}</dt>
              <dd>{formatNumber(state.localModelRefs.length)}</dd>
            </div>
            <div className="tpd-fact tpd-fact-wide">
              <dt>{t('common.description')}</dt>
              <dd className={hardwareType.description ? undefined : 'tpd-muted'}>{hardwareType.description || t('deviceTypes.noDescription')}</dd>
            </div>
          </dl>
        )}
      </section>

      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('deviceTypes.detail.behaviour')}</h3>
          <p>{t('deviceTypes.detail.behaviourIntro')}</p>
        </header>
        {isEditing ? (
          <div className="cu-grid">
            <div className="cu-field cu-field-wide">
              <label htmlFor="hwReboot">{t('deviceTypes.rebootPayload')}</label>
              <input
                type="text"
                id="hwReboot"
                className="cu-input mono"
                data-testid="field-hardware-type-reboot-payload-edit"
                value={state.rebootPayload}
                onChange={(e) => state.setRebootPayload(e.target.value)}
                placeholder="97#"
                maxLength={16}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                disabled={isLoading}
              />
              <span className="cu-hint">{t('deviceTypes.rebootPayloadHelp')}</span>
            </div>
            <div className="cu-field cu-field-wide">
              <label htmlFor="hwRefAnep">{t('deviceTypes.anepReferences')}</label>
              {state.refAnep.length > 0 && (
                <div className="dt-chips">
                  {state.refAnep.map((ref, index) => (
                    <span key={ref} className="dt-chip" data-testid={`ref-anep-${index}`}>
                      {ref}
                      <button type="button" className="dt-chip-remove" onClick={() => state.removeRefAnep(index)} aria-label={t('deviceTypes.detail.remove')} disabled={isLoading}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="cu-input-row">
                <input
                  type="text"
                  id="hwRefAnep"
                  className="cu-input"
                  data-testid="field-hardware-type-ref-anep"
                  value={state.newRefAnep}
                  onChange={(e) => state.setNewRefAnep(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); state.addRefAnep(); } }}
                  placeholder={t('deviceTypes.detail.refAnepPlaceholder')}
                  disabled={isLoading}
                />
                <button type="button" className="btn btn-secondary btn-sm" onClick={state.addRefAnep} disabled={isLoading || !state.newRefAnep.trim()} data-testid="btn-hardware-type-ref-anep-add">
                  {t('deviceTypes.detail.add')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <dl className="tpd-facts">
            <div className="tpd-fact">
              <dt>{t('deviceTypes.rebootPayload')}</dt>
              <dd>
                {hardwareType.reboot_payload ? (
                  <code className="dt-code" data-testid="value-hardware-type-reboot-payload">{hardwareType.reboot_payload}</code>
                ) : (
                  <span className="tpd-muted" data-testid="value-hardware-type-reboot-payload">{t('deviceTypes.rebootPayloadDefault')}</span>
                )}
              </dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('deviceTypes.anepReferences')}</dt>
              <dd>
                {(hardwareType.ref_anep || []).length > 0 ? (
                  <div className="dt-chips">
                    {(hardwareType.ref_anep || []).map((ref) => <span key={ref} className="dt-chip">{ref}</span>)}
                  </div>
                ) : (
                  <span className="tpd-muted">{t('deviceTypes.detail.none')}</span>
                )}
              </dd>
            </div>
          </dl>
        )}
      </section>

      {!isEditing && (
        <section className="cu-section">
          <header className="cu-section-head">
            <h3>{t('deviceTypes.detail.dates')}</h3>
          </header>
          <dl className="tpd-facts">
            <div className="tpd-fact">
              <dt>{t('clients.created')}</dt>
              <dd>{formatDateTime(hardwareType.created_at)}</dd>
            </div>
            <div className="tpd-fact">
              <dt>{t('deviceTypes.lastUpdated')}</dt>
              <dd>{formatDateTime(hardwareType.updated_at)}</dd>
            </div>
          </dl>
        </section>
      )}
    </div>
  );
};
